// src/components/documents/EstimatePage.js — doced-8f3a61c2 (Document editor, Stage 4: the estimate page)
//
// /estimates/new and /estimates/:ref — the full-page estimate, shown only when the company switch
// documentPagesEnabled.estimates is on (CrmApp decides; everyone else keeps today's pop-up).
//
// THE ADDRESS. :ref is the estimate's number (E-0038), or its internal id. Numbers aren't unique
// (legacy timestamp numbers, imports), so the number is looked up (at most 2) AND the id is tried:
// one match opens it, two show a "these share a number" chooser, none says so with a way back.
// A page opened from inside the app carries the id in navigation state, so it never has to guess.
// A new estimate gets its number from the server about a second after its first save; the address
// then changes to the number by itself.
//
// WHAT SHOWS. A new estimate, a Draft, or ?edit=1 → the editor (EstimateEditor). Anything else (Sent,
// Approved, Declined…) → the existing estimate page, QuoteDetailsView, unchanged — Convert, PDF, Resend,
// deposits and the approval breakdown all come with it — and its Edit opens the editor here.
// Estimates have NO edit-after-send lock (founder correction, 2026-10-25): Edit is full editing, the
// deposit locking exactly as it does today.
// /estimates/:ref/send (Stage 5f) → Review & send (EstimateSendPage), reached from the editor's "Review & send"
// button after it saves; the address keeps /send when the number replaces the id.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { doc, collection, query, where, limit, getDocs, getDoc, onSnapshot } from 'firebase/firestore';
import { Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import QuoteDetailsView from '../../views/QuoteDetailsView';
import EstimateEditor from './EstimateEditor';
import EstimateSendPage from './EstimateSendPage'; // doced-8f3a61c2 — Stage 5f
import { withoutGuard } from '../../hooks/useUnsavedGuard';
import { resolveRefMatches, estimatePath } from '../../utils/estimateEditor';
import { normalizeStatus, QUOTE_STATUSES, resolveDocTerminology, DOC_TYPES, isQuoteArchived } from '../../utils/quoteSchema'; // isQuoteArchived: estdel-f9ac8ba8
import { formatCurrency, formatDate, toJsDate } from '../../utils/formatters';

// A Firestore document id can't contain '/' or be '.' / '..'.
const usableAsId = (ref) => !!ref && !ref.includes('/') && ref !== '.' && ref !== '..' && ref.length <= 1500;

function Centered({ children }) {
    return <div className="max-w-xl mx-auto px-4 py-16 text-center text-gray-600 flex flex-col items-center gap-4">{children}</div>;
}

export default function EstimatePage({
    orgId, user, currentUserProfile, companySettings, customers, users, prefill,
    onConvertToInvoice, onConvertToJob, onReQuoteDeclined, onViewJob, isConverting,
    awaitClosePrompt, onClosePromptHandled, onClosingSave,
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const ref = decodeURIComponent(location.pathname.split('/')[2] || '');
    const isNewRoute = ref === 'new';
    // doced-8f3a61c2 — Stage 5f: /estimates/:ref/send is Review & send for that estimate.
    const sendRoute = !isNewRoute && location.pathname.split('/')[3] === 'send';
    const editParam = new URLSearchParams(location.search).get('edit') === '1';
    const stateQuoteId = location.state?.quoteId || null;
    const returnTo = location.state?.returnTo || null;
    const listWord = resolveDocTerminology(companySettings?.quoteTerminologyDefault || DOC_TYPES.QUOTE).wordPlural;
    const quotesPath = `organizations/${orgId}/quotes`;

    const go = (to, opts) => withoutGuard(() => navigate(to, opts));
    const backToList = () => go(returnTo && !returnTo.startsWith('/estimates') ? returnTo : '/quotes');

    // ── which estimate ───────────────────────────────────────────────────────
    const [lookup, setLookup] = useState({ status: 'idle', quotes: [], id: null, error: '' });
    const [lookupNonce, setLookupNonce] = useState(0);
    useEffect(() => {
        if (isNewRoute || !orgId) return undefined;
        if (!ref) { go('/quotes', { replace: true }); return undefined; }
        if (stateQuoteId) { setLookup({ status: 'one', quotes: [], id: stateQuoteId, error: '' }); return undefined; }
        let cancelled = false;
        setLookup({ status: 'loading', quotes: [], id: null, error: '' });
        (async () => {
            try {
                const col = collection(db, quotesPath);
                const asks = [getDocs(query(col, where('quoteNumber', '==', ref), limit(2)))];
                if (/^\d+$/.test(ref) && ref.length < 16) asks.push(getDocs(query(col, where('quoteNumber', '==', Number(ref)), limit(2))));
                const byIdAsk = usableAsId(ref) ? getDoc(doc(db, quotesPath, ref)).catch(() => null) : Promise.resolve(null);
                const [snaps, byIdSnap] = await Promise.all([Promise.all(asks), byIdAsk]);
                if (cancelled) return;
                const byNumber = snaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })));
                const byId = byIdSnap && byIdSnap.exists() ? { id: byIdSnap.id, ...byIdSnap.data() } : null;
                const r = resolveRefMatches(byNumber, byId);
                setLookup({ status: r.kind, quotes: r.quotes, id: r.kind === 'one' ? r.quotes[0].id : null, error: '' });
            } catch (e) {
                if (cancelled) return;
                console.error('doced: estimate lookup failed', e);
                setLookup({ status: 'error', quotes: [], id: null, error: 'The estimate couldn’t be loaded. Check your connection and try again.' });
            }
        })();
        return () => { cancelled = true; };
    }, [isNewRoute, ref, stateQuoteId, orgId, lookupNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── the estimate, live ───────────────────────────────────────────────────
    const quoteId = isNewRoute ? null : lookup.id;
    const [live, setLive] = useState({ id: null, data: null, missing: false });
    useEffect(() => {
        if (!quoteId || !orgId) { setLive({ id: null, data: null, missing: false }); return undefined; }
        setLive({ id: quoteId, data: null, missing: false });
        return onSnapshot(doc(db, quotesPath, quoteId),
            (snap) => setLive(snap.exists() ? { id: quoteId, data: { id: snap.id, ...snap.data() }, missing: false } : { id: quoteId, data: null, missing: true }),
            (e) => { console.error('doced: estimate listener failed', e); setLive({ id: quoteId, data: null, missing: true }); });
    }, [quoteId, orgId]); // eslint-disable-line react-hooks/exhaustive-deps
    const quote = live.id === quoteId ? live.data : null;

    // The number in the address, once there is one.
    useEffect(() => {
        if (!quote || !quote.quoteNumber || String(quote.quoteNumber) === ref) return;
        go(`${estimatePath(quote)}${sendRoute ? '/send' : ''}${location.search}`, { replace: true, state: { ...(location.state || {}), quoteId: quote.id } }); // doced-8f3a61c2 — keeps /send
    }, [quote?.quoteNumber, quote?.id, ref]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── edit or read ─────────────────────────────────────────────────────────
    // Decided once per estimate (a Draft opens in edit), so a status change made elsewhere never
    // swaps the page out from under someone who is typing.
    const [openedAsDraft, setOpenedAsDraft] = useState({});
    useEffect(() => {
        if (quote && openedAsDraft[quote.id] === undefined) {
            setOpenedAsDraft((m) => ({ ...m, [quote.id]: normalizeStatus(quote.status) === QUOTE_STATUSES.DRAFT }));
        }
    }, [quote]); // eslint-disable-line react-hooks/exhaustive-deps
    const draftAtOpen = quote ? (openedAsDraft[quote.id] ?? normalizeStatus(quote.status) === QUOTE_STATUSES.DRAFT) : false;
    // estdel-f9ac8ba8 — a deleted estimate always opens read-only (its banner offers Restore); never the editor or Review & send.
    const isDeleted = !isNewRoute && isQuoteArchived(quote);
    const editing = isNewRoute || ((editParam || draftAtOpen) && !isDeleted);

    // A new estimate's id is minted when the page opens (Notes & media can attach before the first
    // save; the first save writes exactly there) — once per visit to /estimates/new.
    const newRefRef = useRef({ key: null, ref: null });
    if (isNewRoute && newRefRef.current.key !== location.key) {
        newRefRef.current = { key: location.key, ref: orgId ? doc(collection(db, quotesPath)) : null };
    }
    const prefillData = useMemo(() => (
        isNewRoute && prefill && location.state?.prefillKey && prefill.key === location.state.prefillKey ? prefill.data : null
    ), [isNewRoute, prefill, location.state?.prefillKey]);

    // doced-8f3a61c2 — Stage 5f: the editor saved, now Review & send (the page's address carries the id
    // until the number arrives, then switches to the number and keeps /send).
    const toSendPage = (id) => {
        const q = quote && quote.id === id ? quote : null;
        go(`${q ? estimatePath(q) : `/estimates/${encodeURIComponent(id)}`}/send`, { state: { quoteId: id, returnTo } });
    };
    const toEditor = () => go(`${estimatePath(quote)}?edit=1`, { state: { quoteId: quote.id, returnTo } });

    const onSaved = ({ id, isNew, status }) => {
        const stillDraft = normalizeStatus(status) === QUOTE_STATUSES.DRAFT;
        if (!stillDraft) setOpenedAsDraft((m) => ({ ...m, [id]: false }));
        if (isNew) {
            setOpenedAsDraft((m) => ({ ...m, [id]: stillDraft }));
            go(`/estimates/${encodeURIComponent(id)}`, { replace: true, state: { quoteId: id, returnTo } });
        } else if (!stillDraft && editParam) {
            go(location.pathname, { replace: true, state: location.state });
        }
    };

    // ── screens ──────────────────────────────────────────────────────────────
    if (isNewRoute) {
        if (!newRefRef.current.ref) return <Centered><Loader2 className="animate-spin" aria-hidden="true" />Loading…</Centered>;
        return (
            <EstimateEditor
                key={`new-${location.key}`}
                orgId={orgId} user={user} currentUserProfile={currentUserProfile} companySettings={companySettings}
                customers={customers} users={users}
                original={null} prefill={prefillData} newRef={newRefRef.current.ref} live={null}
                listLabel={listWord}
                onSaved={onSaved} onBackToList={backToList} onClosingSave={onClosingSave}
                onReviewAndSend={toSendPage}
            />
        );
    }

    if (lookup.status === 'loading' || lookup.status === 'idle' || (lookup.status === 'one' && !quote && !live.missing)) {
        return <Centered><Loader2 className="animate-spin" aria-hidden="true" /><span>Loading the estimate…</span></Centered>;
    }
    if (lookup.status === 'error') {
        return (
            <Centered>
                <AlertCircle className="text-red-500" aria-hidden="true" />
                <p>{lookup.error}</p>
                <div className="flex flex-wrap justify-center gap-2">
                    <button type="button" onClick={() => setLookupNonce((n) => n + 1)} className="min-h-[44px] px-4 rounded-lg bg-[#5F4EC9] text-white font-semibold">Try again</button>
                    <button type="button" onClick={backToList} className="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white font-semibold">Back to {listWord}</button>
                </div>
            </Centered>
        );
    }
    if (lookup.status === 'none' || (lookup.status === 'one' && live.missing)) {
        return (
            <Centered>
                <AlertCircle className="text-gray-400" aria-hidden="true" />
                <p className="text-gray-800 font-semibold">{lookup.status === 'none' ? `We couldn’t find an estimate called “${ref}”.` : 'This estimate no longer exists — it may have been deleted.'}</p>
                <button type="button" onClick={backToList} className="min-h-[44px] px-4 rounded-lg bg-[#5F4EC9] text-white font-semibold">Back to {listWord}</button>
            </Centered>
        );
    }
    if (lookup.status === 'many') {
        return (
            <div className="max-w-2xl mx-auto px-4 py-10 flex flex-col gap-4">
                <h1 className="text-xl font-bold text-gray-900">Two estimates share the number “{ref}”</h1>
                <p className="text-sm text-gray-600">Older estimates sometimes have the same number. Choose the one you want.</p>
                <ul className="flex flex-col gap-2">
                    {lookup.quotes.map((q) => (
                        <li key={q.id}>
                            <button type="button" onClick={() => go(`/estimates/${encodeURIComponent(ref)}`, { state: { quoteId: q.id, returnTo } })}
                                className="w-full min-h-[64px] px-4 py-3 bg-white border border-gray-200 rounded-xl text-left hover:border-[#5F4EC9] flex items-center justify-between gap-3">
                                <span className="min-w-0">
                                    <span className="block font-semibold text-gray-900 truncate">{q.title || 'Untitled'} — {q.customerName || 'No customer'}</span>
                                    <span className="block text-xs text-gray-500">{normalizeStatus(q.status) || 'No status'} · {formatDate(toJsDate(q.createdAt))} · {formatCurrency(Number(q.total) || 0, companySettings?.currency || 'USD')}</span>
                                </span>
                                <ChevronRight size={18} className="text-gray-400 shrink-0" aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ul>
                <button type="button" onClick={backToList} className="self-start min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white font-semibold">Back to {listWord}</button>
            </div>
        );
    }

    if (!quote) return <Centered><Loader2 className="animate-spin" aria-hidden="true" /></Centered>;

    if (sendRoute && !isDeleted) { // doced-8f3a61c2 — Stage 5f; estdel-f9ac8ba8 — never for a deleted estimate
        return (
            <EstimateSendPage
                key={`send-${quote.id}`}
                orgId={orgId} quote={quote} companySettings={companySettings} currentUserProfile={currentUserProfile} user={user}
                listLabel={listWord} customers={customers}
                onBack={toEditor}
                onBackToList={backToList}
                onDone={() => { setOpenedAsDraft((m) => ({ ...m, [quote.id]: false })); go(estimatePath(quote), { state: { quoteId: quote.id, returnTo } }); }}
            />
        );
    }

    if (editing) {
        return (
            <EstimateEditor
                key={`edit-${quote.id}`}
                orgId={orgId} user={user} currentUserProfile={currentUserProfile} companySettings={companySettings}
                customers={customers} users={users}
                original={quote} prefill={null} newRef={null} live={quote}
                listLabel={listWord}
                onSaved={onSaved}
                onBackToList={backToList}
                onBackToReadView={() => { setOpenedAsDraft((m) => ({ ...m, [quote.id]: false })); go(location.pathname, { replace: editParam, state: location.state }); }}
                onClosingSave={onClosingSave}
                onReviewAndSend={toSendPage}
            />
        );
    }

    return (
        <QuoteDetailsView
            quote={quote}
            companySettings={companySettings}
            onBack={backToList}
            backLabel={returnTo && returnTo.startsWith('/calendar') ? 'Calendar' : null}
            orgId={orgId}
            currentUserProfile={currentUserProfile}
            onEditQuote={() => go(`${location.pathname}?edit=1`, { state: { ...(location.state || {}), quoteId: quote.id } })}
            onConvertToInvoice={onConvertToInvoice}
            onConvertToJob={onConvertToJob}
            onReQuoteDeclined={onReQuoteDeclined}
            onViewJob={onViewJob}
            isConverting={isConverting}
            awaitClosePrompt={awaitClosePrompt}
            onClosePromptHandled={onClosePromptHandled}
        />
    );
}
